import { cn } from "@lib/cn"
import { ChartNoAxesColumnIncreasing } from "lucide-react"
import { forwardRef } from "react"
import { useTranslation } from "react-i18next"

import { useClassroomAdaptiveLevels } from "../hooks"

import type { AdaptiveMode } from "@/types"

interface Props {
  classroomId: string
  /** The classroom's mode from the classroom payload, so the chip can speak
   *  before the adaptive-levels request lands. */
  fallbackMode?: AdaptiveMode
  onOpen: () => void
}

/**
 * The classroom header's door to adaptive levels. Quiet by default
 * ("Adaptativo · Revisión docente"); when level changes wait on the
 * professor it says how many ("3 cambios de nivel por revisar"), in the
 * attention colour, because that is the one adaptive fact she has to act on.
 *
 * Page 1 at the default size is the same request the dialog makes, so opening
 * it costs nothing more; `pending_count` covers every page.
 */
export const AdaptiveLevelsChip = forwardRef<HTMLButtonElement, Props>(function AdaptiveLevelsChip(
  { classroomId, fallbackMode, onOpen },
  ref,
) {
  const { t } = useTranslation("adaptive-levels")
  const query = useClassroomAdaptiveLevels(classroomId)
  const data = query.data
  const mode = data?.adaptive_mode ?? fallbackMode
  if (!mode) return null
  // The whole classroom's count from the server — not page 1's rows, which
  // would miss a recommendation for student 51.
  const pending = data?.pending_count ?? 0

  return (
    <button
      ref={ref}
      type="button"
      onClick={onOpen}
      aria-haspopup="dialog"
      className={cn(
        "inline-flex min-h-11 shrink-0 items-center gap-1.5 rounded-full border px-3 text-xs font-medium whitespace-nowrap transition-colors duration-150 md:min-h-9",
        "focus-visible:ring-accent focus:outline-none focus-visible:ring-2",
        pending > 0
          ? "border-attention bg-attention-soft text-attention-ink hover:border-attention-ink"
          : "border-border bg-surface text-ink-muted hover:bg-inset",
      )}
    >
      {/* Ascending bars: five ordered levels, a step up. The brain it
          replaced said "AI", which is not what this control is about. */}
      <ChartNoAxesColumnIncreasing className="h-4 w-4 shrink-0" aria-hidden />
      {pending > 0 ? (
        <>
          <span className="sr-only">{t("chip.pending_prefix")} </span>
          {t("chip.pending", { count: pending })}
        </>
      ) : (
        t("chip.mode", { mode: t(`mode_short.${mode}`) })
      )}
    </button>
  )
})
